<template lang="pug">
  .reviews.review-wrap(v-if="product.id")
    .container
      .row.xs-f-small.md-f-medium.lg-f-small.max-at-tweak
        .fixed-width-tweak-upper
          .title-wrap.xs-12.text-center
            SectionTitle(v-if="!hideTitle" text="Reviews" data-mr-ass="reviews-section")
            p.desc-short.text-color-2.xs-f-small.lg-f-medium.max-at-tweak.pt-25m.pb-50m(v-if="cmsSettings.perfectPair") You’re viewing reviews
              br
              span  for&nbsp;
              span.semi-bold {{ product.name }}
              span(v-if="selectedPerfectPairGloss && selectedPerfectPairGloss.name")  and&nbsp;
                span.semi-bold {{selectedPerfectPairGloss.name}}
            p.desc-short.text-color-2.xs-f-small.lg-f-medium.max-at-tweak(v-else-if="product.short_description") {{ product.short_description }}

            template(v-if="summary")
              stars.inline-block.xs-f-small.lg-f-medium.max-at-tweak(:rating="summary.average")
              span.text-color-1 {{ summary.count }}

            .write-review.xs-12.xs-pb-150m.text-center.mt-10
              a.mr-btn.btn-inverse.btn-fixed-width.xs-f-xsmall.max-at-tweak(v-if="canReview" :href="productReviewUrl") Write a Review
              mr-btn.review-btn(xxsmall round v-else-if="anonymousReviewEnabled" @click.prevent="openReviewModal(product)")
                span.capitalize Write a Review

          .reviews-info.xs-12.text-center
            StarDistribution

            .average-results(v-if="showAverageResults")
              AverageResult(v-if="showLookAndFeelAverageResult" title="Hair look & feel" left-text="More dry" right-text="Soft & shiny" :field-summary="product.reviewInfo.look_and_feel" :field-summary-order="['more_dry', 'same', 'soft_and_shiny']")
              AverageResult(v-if="showGrayCoverageAverageResult" title="Gray coverage" left-text="Didn't cover grays" right-text="Covered all grays" :field-summary="product.reviewInfo.gray_coverage" :field-summary-order="['didnt_cover_grays', 'covered_some_grays', 'covered_all_grays']")

    #pdp-reviews.container(:class="{'bg-gray-light': showFilters}")
      .row.xs-f-small.md-f-medium.lg-f-small.max-at-tweak
        .fixed-width-tweak-upper
          .sort-by-filters.xs-12(:class="{'text-center': showFilters, 'no-filters': !showFilters}")
            MrDropdown.sort-by(label="Sort By" :options="sortByOptions" v-model="sortBy" data-mr-ass="review-sort")

            button.filters-button.mr-btn.btn-inverse.xs-font-xsmall(type="button" ref="filtersButton" @click="openFilterModal({ returnFocusElement: $refs.filtersButton })" v-if="windowData.breakpoint != 'desktop' && showFilters") filters

            template(v-if="windowData.breakpoint == 'desktop' && showFilters")
              multi-select-dropdown.filter(v-if="showAgeRangeFilter" v-model="ageRangeFilter" :options="ageRangeOptions" title="<span class='ada-tooltip'>Filter by </span>Age range")
              multi-select-dropdown.filter(v-if="showHairTypeFilter" v-model="hairTypeFilter" :options="hairTypeOptions" title="<span class='ada-tooltip'>Filter by </span>Hair Type")
              multi-select-dropdown.filter(v-if="showGrayAmountFilter" v-model="grayAmountFilter" :options="grayAmountOptions" title="<span class='ada-tooltip'>Filter by </span>Amount of gray")

    .container
      .row.xs-f-small.md-f-medium.lg-f-small.max-at-tweak
        .fixed-width-tweak-upper
          FiltersApplied

          .review-page.xs-12
            .xs-12.text-center(v-if="!reviews || (!reviews.length && hasFilters)")
              p.no-reviews-message.text-color-2.xs-f-small.md-f-medium.max-at-tweak Sorry, no reviews match all of the above criteria. Try removing one or more filters.

            Review.xs-12(v-for="review in reviews" :key="review.id" :review="review" :product-name="product.name")

          .pagination.xs-12
            template(v-if="page > 1")
              router-link.back(:to="{ name: 'pdpPage', hash: '#pdp-reviews', params: { urlKey: product.url_key }, query: { reviews: page - 1}}")
                i.icon-caret-left
                  span.ada-tooltip caret left icon
                span.back-text Back

            span &nbsp;{{ page }}/{{ pages }}&nbsp;

            template(v-if="page !== pages")
              router-link.next(:to="{ name: 'pdpPage', hash: '#pdp-reviews', params: { urlKey: product.url_key }, query: { reviews: page + 1 }}")
                span.next-text Next
                i.icon-caret-right
                  span.ada-tooltip caret right icon

</template>

<script>
  import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
  import vueReviewSvc from '@services/vueReviewSvc';

  import SectionTitle from "@components/SectionTitle";
  import MrDropdown from "@components/MrDropdown";
  import AverageResult from "./AverageResult";
  import FiltersApplied from "./FiltersApplied";
  import Review from "./Review";
  import StarDistribution from "./StarDistribution";
  import MrBtn from '@components/MrBtn';

  import { isNum } from '@utilities/mrVueUtils';

  export default {
    name: "Reviews",

    components: {
      SectionTitle,
      MrDropdown,
      AverageResult,
      FiltersApplied,
      Review,
      StarDistribution,
      MrBtn
    },

    props: {
      hideTitle: {
        type: Boolean,
        required: false,
        default: false
      },
      defaultSortBy: {
        type:String,
        required: false,
        default: null
      }
    },

    data() {
      return {
        canReview: false
      };
    },

    computed: {
      ...mapState('cms', ['cmsSettings']),
      ...mapState('pdp', ['product', 'selectedPerfectPairGloss', 'addPerfectPair']),
      ...mapGetters('pdp', ['reviewInfoAvailable']),
      ...mapState('reviews', ['summary', 'reviews', 'page', 'pages', 'sortByOptions', 'sortByChoices', 'filters']),
      ...mapGetters('reviews', ['hasFilters', 'ratingFilter', 'ageRangeOptions', 'hairTypeOptions', 'grayAmountOptions']),
      ...mapState('systemSettings', ['anonymousReviewSettings']),

      ...mapState('global', [
        'windowData'
      ]),

      reviewProductIds() {
        if (this.product.id) {
          var productIds = [this.product.id];

          if (this.product.associatedReviewProductIds) {
            var associatedReviewProductIds = this.product.associatedReviewProductIds.split(',').filter(id => isNum(+id)).map(id => +id);
            productIds = productIds.concat(associatedReviewProductIds);
          }

          if (this.cmsSettings.perfectPair && this.addPerfectPair && this.selectedPerfectPairGloss && this.selectedPerfectPairGloss.id) {
            productIds = [...productIds, this.selectedPerfectPairGloss.id];
          }

          return productIds;
        } else {
          return null;
        }
      },

      sortBy: {
        get() {
          return this.$store.state.reviews?.sortBy;
        },

        set(val) {
          this.setSortBy(val);
        }
      },

      showAverageResults() {
        return this.showLookAndFeelAverageResult || this.showGrayCoverageAverageResult;
      },

      showLookAndFeelAverageResult() {
        return this.cmsSettings.hairLookAndFeelAverageResult && this.reviewInfoAvailable('look_and_feel');
      },

      showGrayCoverageAverageResult() {
        return this.cmsSettings.grayCoverageAverageResult && this.reviewInfoAvailable('gray_coverage');
      },

      ageRangeFilter: {
        get() {
          return this.$store.getters['reviews/ageRangeFilter'];
        },

        set(val) {
          this.setFilter({
            filter: 'ageRange',
            value: val
          });
        }
      },

      hairTypeFilter: {
        get() {
          return this.$store.getters['reviews/hairTypeFilter'];
        },

        set(val) {
          this.setFilter({
            filter: 'hairTypes',
            value: val
          });
        }
      },

      grayAmountFilter: {
        get() {
          return this.$store.getters['reviews/grayAmountFilter'];
        },

        set(val) {
          this.setFilter({
            filter: 'grayAmount',
            value: val
          });
        }
      },

      showAgeRangeFilter() {
        return this.cmsSettings.ageRangeFilter && this.reviewInfoAvailable('age_range');
      },

      showHairTypeFilter() {
        return this.cmsSettings.hairTypeFilter && this.reviewInfoAvailable('hair_type');
      },

      showGrayAmountFilter() {
        return this.cmsSettings.grayAmountFilter && this.reviewInfoAvailable('gray_amount');
      },

      showFilters() {
        return this.showAgeRangeFilter || this.showHairTypeFilter || this.showGrayAmountFilter;
      },

      productReviewUrl() {
        return `/product/${this.product.url_key}/review`;
      },

      baseUrl() {
        let loc = window.location;
        return loc.protocol + '//' + loc.host + loc.pathname;
      },

      prevUrl() {
        return this.page === 1 ? '' : this.page === 2 ? this.baseUrl : this.baseUrl + '?reviews=' + (this.page - 1);
      },

      nextUrl() {
        return this.page < this.totalPages ? this.baseUrl + '?reviews=' + (this.page + 1) : '';
      },

      anonymousReviewEnabled() {
        return this.anonymousReviewSettings?.enabled;
      },

      canGetProductReviews() {
        return [
          [this.sortBy],
          this.reviewProductIds,
          this.ratingFilter,
          this.ageRangeFilter,
          this.hairTypeFilter,
          this.grayAmountFilter,
          this.page,
        ];
      },

      productId() {
        return this.product?.id;
      },
    },

    watch: {
      '$route.query.reviews'() {
        this.setPage(this.$route.query.reviews || 1);
        this.handlePaginationSEO();
      },

      productId: {
        async handler(newProductId, oldProductId) {
          if (newProductId && newProductId !== oldProductId) {
            this.checkCanUserReview();
          }
        },
      },

      canGetProductReviews: {
        async handler(val) {
          this.checkCanGetProductReviews(val);
        },
        deep: true,
      }
    },

    mounted() {
      //- check reviews query for page
      let reviewsQuery = this.$route.query.reviews;

      if (isNum(+reviewsQuery) && +reviewsQuery > 1) {
        this.setPage(+reviewsQuery);
      }

      if (this.product.product_type == 'root_touch_up') {
        this.setSortBy(this.sortByChoices.NEWEST);
      }
      this.getAnonymousReviewSettings();
      
      // ONLY ON CLIENT SIDE
      this.checkCanUserReview();
      this.checkCanGetProductReviews(this.canGetProductReviews);
    },

    methods: {
      ...mapMutations('reviews', ['setSortBy', 'setFilter', 'setPage']),
      ...mapActions('reviews', ['getProductReviewsForPdp', 'openFilterModal']),
      ...mapActions('systemSettings', ['getAnonymousReviewSettings']),
      ...mapActions('modal', ['showModal']),

      async checkCanGetProductReviews(canGetProductReviews) {
        if (canGetProductReviews.every(Boolean)) {
          const data = await this.getProductReviewsForPdp({ productIds: this.reviewProductIds });
          // redirect to page 1 of reviews if no reviews and not on page 1
          if (data.reviews.length == 0 && this.page != 1) {
            await this.$router.push({ path: window.location.pathname + '?reviews=1' });
          }
        }
      },

      async checkCanUserReview() {
        const res = await vueReviewSvc.canUserReview({ product_id: this.product.id });
        this.canReview = res.data.canReview;
      },

      handlePaginationSEO() {
        let thisUrl = this.baseUrl;

        if (this.page !== 1) {
          thisUrl += '?reviews=' + this.page;
        }

        let canonicalLink = document.querySelector('link[rel=canonical]');
        if (canonicalLink) {
          canonicalLink.setAttribute('href', thisUrl);
        }

        let prevLink = document.querySelector('link[rel=prev]');
        if (this.prevUrl) {
          if (prevLink) {
            prevLink.setAttribute('href', this.prevUrl);
          } else {
            let headPrevLink = document.createElement('link');
            headPrevLink.setAttribute('rel', 'prev');
            headPrevLink.setAttribute('href', this.prevUrl);
            let headEl = document.querySelector('head');
            headEl.appendChild(headPrevLink);
          }
        } else if (prevLink) {
          prevLink.remove();
        }

        let nextLink = document.querySelector('link[rel=next]');
        if (this.nextUrl) {
          if (nextLink) {
            nextLink.setAttribute('href', this.nextUrl);
          } else {
            let headNextLink = document.createElement('link');
            headNextLink.setAttribute('rel', 'next');
            headNextLink.setAttribute('href', this.nextUrl);
            let headEl = document.querySelector('head');
            headEl.appendChild(headNextLink);
          }
        } else if (nextLink) {
          nextLink.remove();
        }
      },

      openReviewModal() {
        this.showModal({
          component: 'EditReviewModal',
          props: {
            product: this.product
          },
          theme: 'app-modal-overflow-visible',
        });
      }
    }
  };
</script>

<style scoped lang="stylus">
  .reviews
    margin-bottom 1em

    .title-wrap
    .reviews-info
    .write-review
    .sort-by-filters
      padding-left 3%
      padding-right 3%

    .write-review
      display flex
      justify-content center

      >>>.mrbtn
        padding 0 0.5em
        background-color cta-color-4
        font-weight 700
        line-height 14px
        letter-spacing -0.24px

        span
          padding 6px 0.5em

    .review-page
    .pagination
      padding-left 5%
      padding-right 5%

    .title-wrap
      padding-top 1.5em
      padding-bottom 1.5em

    .reviews-info
      padding-bottom 1.5em

      .average-results
        width 100%
        display inline-block
        vertical-align top

    .sort-by-filters
      padding-top 1em
      padding-bottom 1em

      .sort-by
      .filter
        display inline-block
        vertical-align top
        margin 0

      >>>.sort-by
        width 13.5em

        .dropdown-wrap
          border-color #9b9b9b

      .filters-button
        height 44px
        margin-left 1em

      .filter
        width 13.5em
        margin-left 1.5em

    .review-page
      margin 2em 0 1em

      .no-reviews-message
        padding 0 5%
        padding-bottom 1.5em

    .pagination
      position relative
      text-align right
      margin-top 20px
      margin-right 20px
      margin 20px 20px 0 0

      a
        position relative
        text-decoration underline
        text-transform capitalize

        &.back
          margin-right 20px
        &.next
          margin-left 20px

        .next-text
          margin-right 3px
          pading-right 20px

        .back-text
          margin-left 3px

        i
          font-size 9px

          &.icon-caret-right
            position absolute
            right -13px
            top 0

          &.icon-caret-left
            position absolute
            left -13px
            top 0

    @media mq-mobile
      padding 0

      .sort-by-filters
        .sort-by
          width 12em

    @media mq-tablet
      .review-page
        .no-reviews-message
          padding 0 25%
          padding-bottom 1.5em

    @media mq-desktop-md-plus
      .reviews-info
        .average-results
          width 50%

    @media mq-desktop-md
      .review-page
        .no-reviews-message
          padding 0 30%
          padding-bottom 1.5em

    @media mq-desktop-plus
      .sort-by-filters
        .sort-by
        .filter
          width 14.5em

      .review-page
        .no-reviews-message
          padding 0 33%
          padding-bottom 1.5em

</style>
